import { Fetcher, StrictFetcher, assertNever, getFirstFromArray, isAiTravel, trackClick } from '@this/src/util';
import React from 'react';
import _ from 'lodash';

import { styled } from '@this/constants/themes';
import { css, withTheme } from 'styled-components';
import type { GeolocationProps } from '@this/components/deprecated/geolocationAdapter';
import { withGeolocation } from '@this/components/deprecated/geolocationAdapter';

import { Box } from '@material-ui/core';
import { Button, BUTTON_COLOR } from '@this/shared/ui/inputs/button';
import { Text } from '@this/components/shared/ui/data_displays/typography';
import { getSpacing } from '@this/components/shared/ui/theme';

import localStorage from '@this/lib/local_storage';
import { reportError } from '@this/lib/bugsnag';
import moment from '../../../lib/moment';

import SearchRoundTrip from './search_round_trip';
import SearchOneWayTemplate from './search_one_way.template';
import SearchMultipleLocationsTemplate from './search_multiple_locations.template';
import SearchHotelTripTemplate from './search_hotel.template';
import RentalCarTripTemplate from './search_rental_car_trip.template';
import SearchTypeTabs from './search_type_tabs';
import type { SearchTypeTabItem } from './search_type_tabs';
import type { SearchProps, SearchState } from './types';
import SearchQuery from '../../../domain/search_query';

const SEARCH_TAB_ITEMS = [
  { index: 0, label: '往復', icon: 'roundTrip' },
  { index: 1, label: '片道', icon: 'oneWay' },
  { index: 2, label: '複数拠点', icon: 'multipleLocations' },
  { index: 3, label: 'ホテル', icon: 'hotel' },
  { index: 4, label: 'レンタカー', icon: 'rentalCar' }
] as const;
const MICE_INQUIRY_TAB_INDEX = 5;

const getRentalcarAvailable = (query: SearchQuery, user?: SearchProps['user']) =>
  _.isUndefined(query.rentalcarAvailable) ? user?.organization?.enable_rentalcar_form : query.rentalcarAvailable;

interface CarTypeResponse {
  car_type: [string, string][];
}

interface ExtendedSearchProps extends SearchProps, GeolocationProps {}

class Search extends React.Component<ExtendedSearchProps, SearchState> {
  constructor(props: ExtendedSearchProps) {
    super(props);

    this.state = {
      query: this.props.query
        ? this.props.query
        : new SearchQuery(
            Object.assign(utils.getParams(), {
              cabin: (this.props.defaultSeatClasses || []).join(',')
            })
          ),
      errors: [],
      addItemEnable: true
    };
  }

  handleTabChange = (i: number) => {
    if (i === MICE_INQUIRY_TAB_INDEX) {
      window.open('/mice_inquiries/new', '_blank', 'noopener,noreferrer');
      return;
    }
    this.state.query.setCurrentTabIndex(i);
  };

  handleChange =
    (name: 'peoplenum' | 'roomnum' | 'smoke' | 'breakfast') => (e: React.ChangeEvent<HTMLSelectElement>) => {
      e.preventDefault();
      const value = e.target.value;
      switch (name) {
        case 'breakfast':
          this.state.query.setBreakfast(value);
          break;
        case 'peoplenum':
          this.state.query.setPeoplenum(Number(value));
          break;
        case 'roomnum':
          this.state.query.setRoomnum(Number(value));
          break;
        case 'smoke':
          this.state.query.setSmoke(value);
          break;
        default: {
          // nameが増えてcaseがないとtscでエラーになるようにするため
          assertNever(name);
          break;
        }
      }
    };

  handleAddItem = (itemType: 'transport' | 'hotel' | 'rentalCar') => (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (!this.state.query.isItemLimit()) {
      this.state.query.addItem(itemType);
    }
    this.updateAddItemEnable();
  };

  handleRemoveItem = (index: number) => {
    this.state.query.removeItem(index);
    this.updateAddItemEnable();
  };

  private updateAddItemEnable() {
    if (this.state.query.isItemLimit()) {
      this.setState({ addItemEnable: false });
      return;
    }
    this.setState({ addItemEnable: true });
  }

  handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    trackClick('/dashboard search button');
    this.setState({
      loading: true,
      submitting: true,
      errors: []
    });

    try {
      this.state.query.validationPromise();
      this.storeQueries();
      this.clearHotelSearchConditions();
      // 航空会社で絞り込んだあとに別の条件で検索すると航空会社の条件まで加味してしまうため
      this.state.query.clearCarrier();
      this.setState(
        {
          submitting: false,
          loading: false
        },
        () => {
          this.props.handleSearch(this.state.query);
        }
      );
    } catch (errors) {
      this.setState({
        errors,
        submitting: false,
        loading: false
      });
    }
  };

  componentDidMount() {
    Search.rebuildTextInputEvent();
    this.fetchCarType();
    this.fetchUser();
    this.updateAddItemEnable();
  }

  UNSAFE_componentWillUpdate() {
    // bownown内でlocaleをenに変更されてしまうので
    // bownowは利用をやめたが、他に影響が出ると怖いので下記の処理はおいてある
    if (moment.locale() === 'en') {
      moment.locale('ja');
    }
  }

  static rebuildTextInputEvent() {
    if (!utils.iOS()) {
      return;
    }

    // 以下の問題に対する処置として、textInput後のkeydown, keyupを再定義する
    // - iOSでサジェストが効かない問題
    // - キーボードの確定ボタンで検索すると変換前の文字に戻ってしまう
    // eslint-disable-next-line func-names
    $('#origin, #destination').on('textInput', function () {
      ['keydown', 'keyup'].forEach(keyEvent => {
        const e = document.createEvent('KeyboardEvent');
        (e as any).initKeyboardEvent(keyEvent, true, true, window, 'Enter', 0, '');
        this.dispatchEvent(e);
      });
    });
  }

  private fetchCarType() {
    Fetcher.get<CarTypeResponse>('/car_types.json').then(result => {
      this.state.query.setCarType(result.car_type);
    });
  }

  private fetchUser() {
    StrictFetcher.get<any>('/users.json', 'users/index.json').then(result => {
      this.state.query.setDefaultSeatClasses(result.default_seat_classes);
    });
  }

  getCurrentPlace = (evt: React.MouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault();

    this.props
      .getCurrentPlace(evt)
      .then(({ formattedAddress }) => {
        const { query } = this.state;
        const items = query.items;

        const departureTrip = getFirstFromArray(items, 'getCurrentPlace-origin');
        if (departureTrip && typeof departureTrip.setOrigin === 'function') {
          departureTrip.setOrigin(formattedAddress);
        }
        if (this.state.query.currentTabIndex === 0) {
          const returnTrip = items.filter(item => item.itemType === 'transport').pop();
          if (returnTrip && typeof returnTrip.setDestination === 'function') {
            returnTrip.setDestination(formattedAddress);
          }
        }
      })
      .catch(error => {
        this.setState({
          errors: [error.message || '位置情報の取得に失敗しました。']
        });
      });
  };

  private storeQueries() {
    this.state.query.items.forEach((item: any) => {
      ['origin', 'destination'].forEach(key => {
        if (_.isEmpty(item[key])) return;

        Search.storeData(key, {
          text: item[key],
          address: item[`${key}Address`],
          addDate: _.now()
        });
      });
    });
  }

  private clearHotelSearchConditions() {
    localStorage.removeItem('breakfast');
    localStorage.removeItem('smoke');
  }

  private static storeData(key: string, data: { text: string; address: string; addDate: number }) {
    const rawdata = localStorage.getItem(key);
    let preData = rawdata ? JSON.parse(rawdata) : rawdata;
    if (!preData || !_.isArray(preData)) {
      preData = [];
    }
    _.remove(preData, (o: any) => o.text === data.text);
    preData.push(data);
    // 一旦履歴は50件に制限する
    if (preData.length >= 50) {
      preData = preData.slice(preData.length - 50, preData.length);
    }
    localStorage.setItem(key, JSON.stringify(preData));
  }

  handleRentalCarInfoAdd = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    this.state.query.addItem('rentalCar');
  };

  render() {
    try {
      const { query, loading, submitting, errors } = this.state;
      const { geolocationError, serviceId } = this.props;
      const rentalcarAvailable = getRentalcarAvailable(query, this.props.user);

      const enableJustBeforeHotel = this.props.user?.organization?.enable_just_before_reserve_expedia_hotel;
      const items: SearchTypeTabItem[] = SEARCH_TAB_ITEMS.filter(item => {
        if (item.index === 3 && query.disabledHotelSearch) return false;
        if ((item.index === 0 || item.index === 1) && query.disabledTransitSearch) return false;
        if (item.index === 2 && query.disabledHotelSearch && query.disabledTransitSearch) return false;
        if (item.index === 4 && rentalcarAvailable !== true) return false;
        if (location.pathname.match(/trips\/new|trips\/\d+\/edit/)) {
          return item.index !== 2 && item.index !== 4;
        }
        return true;
      }).map(item => {
        if (
          item.index === 3 &&
          enableJustBeforeHotel &&
          this.props.user?.service != null &&
          isAiTravel(this.props.user.service.id)
        ) {
          return { ...item, badge: '当日予約OK' };
        }
        return item;
      });

      if (serviceId != null && isAiTravel(serviceId) && !location.pathname.match(/trips\/new|trips\/\d+\/edit/)) {
        items.push({ index: MICE_INQUIRY_TAB_INDEX, label: '団体', icon: 'miceInquiry' });
      }

      return (
        <form className="search" action="/select" method="GET" onSubmit={this.handleSubmit}>
          <div
            style={{ display: 'flex', alignItems: 'stretch', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}
          >
            <SearchTypeTabs currentIndex={query.currentTabIndex} items={items} onChange={this.handleTabChange} />
            {this.props.onClickShowSearchHistories && (
              <div className="search-history-button-wrapper">
                <Button color={BUTTON_COLOR.SUB} onClick={this.props.onClickShowSearchHistories} fullWidth>
                  検索履歴
                </Button>
              </div>
            )}
          </div>
          {query.currentTabIndex === 0 ? (
            <SearchRoundTrip
              {...this.props}
              {...this.state}
              query={query}
              handleChange={this.handleChange}
              handleRemoveItem={this.handleRemoveItem}
              handleAddItem={this.handleAddItem}
              getCurrentPlace={this.getCurrentPlace}
            />
          ) : query.currentTabIndex === 1 ? (
            <SearchOneWayTemplate
              {...this.props}
              {...this.state}
              query={query}
              handleChange={this.handleChange}
              handleRemoveItem={this.handleRemoveItem}
              handleAddItem={this.handleAddItem}
            />
          ) : query.currentTabIndex === 2 ? (
            <SearchMultipleLocationsTemplate
              {...this.props}
              {...this.state}
              query={query}
              handleChange={this.handleChange}
              handleRemoveItem={this.handleRemoveItem}
              handleAddItem={this.handleAddItem}
            />
          ) : query.currentTabIndex === 3 ? (
            <SearchHotelTripTemplate
              {...this.props}
              {...this.state}
              query={query}
              handleChange={this.handleChange}
              handleRemoveItem={this.handleRemoveItem}
              handleAddItem={this.handleAddItem}
            />
          ) : query.currentTabIndex === 4 ? (
            <RentalCarTripTemplate
              {...this.props}
              {...this.state}
              query={query}
              handleChange={this.handleChange}
              handleRentalCarInfoAdd={this.handleRentalCarInfoAdd}
              handleRemoveItem={this.handleRemoveItem}
            />
          ) : null}
          {loading && <img className="select__loading" src="/images/loading.gif" width="50" height="50" />}
          <div className="search__button-wrapper">
            <SearchError>
              {_.entries(errors).map(([key, error]) => (
                <Text key={key} color="danger">
                  {error}
                </Text>
              ))}

              {geolocationError && <Text color="danger">{geolocationError}</Text>}
            </SearchError>
            <Box m="0 auto" maxWidth="425px">
              <Button id="search-button" type="submit" size="large" fullWidth disabled={submitting}>
                {query.currentTabIndex === 4 ? '依頼する' : '検索'}
              </Button>
            </Box>
          </div>
        </form>
      );
    } catch (e) {
      reportError(e);
      return null;
    }
  }
}

export const OutLabelWrap = styled.div`
  margin: 3px auto 0 7px;
`;

export const OutLabel = styled.label`
  margin-bottom: 0.75em;
  font-weight: bold;
  font-size: 13px;
`;

export const InLabel = styled.label`
  color: ${props => props.theme.linkColor};
  font-weight: bold;
  font-size: 12px;
`;

export const FieldsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: 0;
`;

export const Field = styled.div`
  flex-grow: 99999;
  flex-basis: 230px;
  background: ${props => props.theme.fieldBgColor};
  border: 2px solid ${props => props.theme.fieldBorderColor};
  padding: 3px 5px 3px 10px;
  margin-top: 5px;
  width: 100%;
`;

export const ToggleField = styled(Field)<{ disabled: boolean }>`
  background: ${props => (props.disabled ? props.theme.grayBgColor : props.theme.fieldBgColor)};
`;

export const InputArea = styled.div<{ wrapped: boolean }>`
  display: flex;
  align-items: flex-end;
  flex-wrap: ${props => (props.wrapped ? 'wrap' : 'nowrap')};
`;

export const AddressArea = styled.div`
  color: ${props => props.theme.grayTextColor};
  font-weight: bold;
  font-size: 11px;
`;

const InputBase = css`
  border: 0;
  padding: 0 15px 0 0;
  margin-bottom: 0;
  box-shadow: none;
  line-height: 1;
  background: ${props => props.theme.fieldBgColor};
  box-shadow: none;
  cursor: pointer;
`;

export const TextField = styled.input`
  &&& {
    ${InputBase}
    padding-top: 2px;
    &:focus {
      box-shadow: none;
    }
    &:disabled {
      display: none;
    }
  }
`;

export const SelectWrap = styled.div`
  flex-basis: 105px;
`;

export const Select = styled.select`
  ${InputBase}
  padding: 0;
  appearance: none;
  background: ${props => props.theme.fieldBgColor};

  &:focus {
    outline: 0;
  }

  option {
    appearance: none;
  }
`;

const SearchError = styled.div`
  padding: ${getSpacing(2)}px 0;
`;

export default withTheme(withGeolocation(Search));
